import React, { useState, useEffect, useRef, PropsWithChildren } from "react"
import { User, LogOut, CheckSquare, Square, ChevronUp } from "react-feather"
import { Link, useLocation } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { loadEnvVars } from "../../utils/loadEnvVars"
import { spellOutForScreenReader } from "../../utils/spellOutForScreenReader"
import Logo from "@bancacfplus/Icons/assets/logo-horizontal.svg?react"
import { Menu as Nav } from "react-feather"
import { TokenData } from "../../hooks/useTokenData"
import style from "./Menu.module.scss"
import { brand } from "../../consts/brand"

export type User = {
  name: string
  initials: string
  fullName: string
  companies: Company[]
  email: string
  mobile: string
  data: TokenData
}

export type Company = {
  name: string
  vatCode: string
  active?: boolean
}

type SubMenuContainerProps = PropsWithChildren<{
  isMobileOpened: boolean
  setIsMobileOpened: React.Dispatch<React.SetStateAction<boolean>>
}>

type MenuProps = {
  primaryMenu?: {
    text: string
    active?: boolean | undefined
    icon?: React.ReactNode | undefined
    external: boolean
    url: string
  }[]
  secondaryMenu?: {
    text: string
    url: string
    icon: React.ReactNode
  }[]
  user?: User
}

type MenuPrimaryProps = {
  primaryMenu: {
    text: string
    active?: boolean | undefined
    icon?: React.ReactNode | undefined
    external: boolean
    url: string
    visible?: boolean
  }[]
}

type MenuSecondaryProps = {
  user: User
  secondaryMenu: {
    text: string
    url: string
    icon: React.ReactNode
  }[]
  isMobileOpened: boolean
}

const MenuSecondary: React.FC<MenuSecondaryProps> = ({ user, secondaryMenu, isMobileOpened }) => {
  const [submenuOpened, setSubmenuOpened] = useState(false)
  const [activeCompany, setActiveCompany] = useState<Company | undefined>(
    user?.companies.find(company => company.active) ?? user?.companies[0]
  )
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { logout } = useAuth0()
  const { auth0RedirectUri: logoutURl } = loadEnvVars()

  useEffect(() => {
    function handleClickOutside(event: Event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setSubmenuOpened(false)
      }
    }

    function handleTab(event: KeyboardEvent) {
      if (event.key === "9" && event.target && (event.target as Element).id === "submenu-dropdown") {
        setSubmenuOpened(!submenuOpened)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleTab)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleTab)
    }
  }, [wrapperRef, submenuOpened])

  const changeActiveCompany = (newActiveCompany: Company) => {
    setActiveCompany(newActiveCompany)
  }

  return (
    <>
      <li className={`${style.item} ${style.itemDivider}`} tabIndex={-1}>
        <a
          href={brand.supportPhoneHref}
          className={`${style.link} ${style.lightLink} ${style.phoneCta}`}
          tabIndex={-1}
          aria-label={`Contatta il supporto telefonico al ${brand.supportPhone}`}
        >
          ☎ {brand.supportPhone}
        </a>
      </li>
      <li className={`${style.item} ${style.mobileOnly}`} tabIndex={0}>
        <div className={style.link}>
          <button className={style.subMenulink} onClick={() => logout({ logoutParams: { returnTo: logoutURl } })}>
            <p className={style.subMenuItem}>Esci</p>
            <LogOut className={`${style.subMenuIcon} ${style.iconEnd}`} size={24} aria-hidden="true" />
          </button>
        </div>
      </li>
      <li className={`${style.item} ${style.subMenuItem}`} id="submenu-dropdown">
        <div ref={wrapperRef}>
          <button
            className={`${style.subMenuCta} ${submenuOpened ? style.isActive : ""}`}
            aria-label={`Account di ${user.fullName}`}
            aria-expanded="false"
            aria-controls="submenu-dropdown"
            onClick={() => setSubmenuOpened(!submenuOpened)}
            data-test-id="menu-account"
          >
            <span className={`${style.link} ${style.lightLink} ${style.subMenuCtaText}`}>
              <User className={style.subMenuIcon} size={24} aria-hidden="true" />
              {user.fullName}
            </span>
          </button>
          <div
            className={`${style.subMenu} ${isMobileOpened ? style.isOpenedMobile : ""}`}
            aria-hidden={!submenuOpened}
          >
            <div className={style.subMenuGroup}>
              {user.companies.length > 1 ? (
                <div className={style.accordionWrapper}>
                  <input type="checkbox" id="accordionOpener" className={style.accordionOpener} />
                  <div className={style.subMenuButton}>
                    <label htmlFor="accordionOpener" className={style.accordionLabel} />
                    <p className={style.subMenuItemAccent}>{user.fullName}</p>
                    <p className={style.companyName}>{activeCompany?.name}</p>
                    <ChevronUp size={24} className={style.subMenuAccordionIcon} aria-hidden="true" />
                  </div>
                  <div className={`${style.subMenuGroup} ${style.accordion}`}>
                    {user.companies.map(company => {
                      const isActive = company.vatCode === activeCompany?.vatCode
                      return (
                        <div
                          key={`id-${company.vatCode}`}
                          className={`${style.companyDetails} ${isActive ? style.isActive : ""}`}
                          onClick={() => changeActiveCompany(company)}
                        >
                          {isActive ? <CheckSquare aria-hidden="true" /> : <Square aria-hidden="true" />}
                          <div className={style.companyInfo}>
                            <p className={style.companyName}>{company.name}</p>
                            <p
                              className={style.vatCode}
                              aria-label={`Partita IVA ${spellOutForScreenReader(company.vatCode)}`}
                            >
                              P. IVA {company.vatCode}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className={style.subMenuButton}>
                  <p className={style.subMenuItemAccent}>{user.fullName}</p>
                  <p className={style.companyName}>{activeCompany?.name}</p>
                  <p
                    className={style.vatCode}
                    aria-label={`Partita IVA ${spellOutForScreenReader(activeCompany?.vatCode)}`}
                  >
                    P. IVA {activeCompany?.vatCode}
                  </p>
                </div>
              )}
            </div>
            {secondaryMenu && (
              <div className={style.subMenuGroup}>
                {secondaryMenu.map(link => {
                  return (
                    <Link to={link.url} className={style.subMenulink} key={`id-${link.text}`} aria-label={link.text}>
                      <span className={style.subMenuIcon}>{link.icon}</span>
                      {link.text}
                    </Link>
                  )
                })}
              </div>
            )}
            <div className={`${style.subMenuGroup} ${style.desktopOnly}`}>
              <button className={style.subMenulink} onClick={() => logout({ logoutParams: { returnTo: logoutURl } })}>
                <p className={style.subMenuItem}>Esci</p>
                <LogOut className={`${style.subMenuIcon} ${style.iconEnd}`} size={24} />
              </button>
            </div>
          </div>
        </div>
      </li>
    </>
  )
}

const MenuPrimary: React.FC<MenuPrimaryProps> = ({ primaryMenu }) => {
  const { pathname } = useLocation()
  const rootPath = pathname.split("/")[1].replace("/", "")
  const page = primaryMenu?.findIndex(page => page.url === `/${rootPath}`)
  const [activePage, setActivePage] = useState<number>(page || 0)
  const visiblePages = primaryMenu?.filter(page => page.visible === true)

  const toggleClass = (indexPage: number) => {
    setActivePage(indexPage)
  }

  return (
    <>
      {visiblePages ? (
        visiblePages?.map((link, index) => {
          return (
            <li
              className={`${style.item} ${index + 1 === visiblePages.length ? style.primaryLinksDivider : ""}`}
              key={`id-${link.text}`}
              tabIndex={-1}
            >
              {!link.external ? (
                <a
                  href={link.url}
                  className={`${style.link} ${index === activePage ? style.isActive : ""}`}
                  onClick={() => toggleClass(index)}
                >
                  {link.text}
                </a>
              ) : (
                <Link
                  to={link.url}
                  className={`${style.link} ${index === activePage ? style.isActive : ""}`}
                  onClick={() => toggleClass(index)}
                >
                  <span className={style.linkIconWrapper}>
                    {link.icon && <span className={style.linkIcon}>{link.icon}</span>}
                    {link.text}
                  </span>
                </Link>
              )}
            </li>
          )
        })
      ) : (
        <></>
      )}
    </>
  )
}

export const MenuLogo: React.FC = () => {
  return (
    <Link to="/" className={style.logo} tabIndex={0}>
      <div className={`${style.logoWrapper}`} role="button" aria-label="Logo Banca Sistema">
        <Logo className={style.logoIcon} />
      </div>
    </Link>
  )
}

const SubMenuContainer: React.FC<SubMenuContainerProps> = ({ isMobileOpened, setIsMobileOpened, children }) => {
  return (
    <>
      <div className={`${style.linksContainer} ${isMobileOpened ? style.isOpened : ""}`}>
        <ul className={style.navItems}>{children}</ul>
      </div>
      <button
        type="button"
        onClick={() => setIsMobileOpened(!isMobileOpened)}
        className={`${style.navToggler}`}
        aria-label="Menù"
      >
        <Nav />
        <span className={style.navText}>Menù</span>
      </button>
    </>
  )
}

export const MenuWrapper = ({ children }: PropsWithChildren<object>): React.JSX.Element => {
  return <nav className={style.navHeader}>{children}</nav>
}

const Menu: React.FC<MenuProps> = ({ primaryMenu, secondaryMenu, user }) => {
  const [isMobileOpened, setIsMobileOpened] = useState(false)

  useEffect(() => {
    function handleWindowResize() {
      if (window.innerWidth > 840 && isMobileOpened) {
        setIsMobileOpened(false)
      }
    }
    window.addEventListener("resize", handleWindowResize)
    return () => window.removeEventListener("resize", handleWindowResize)
  }, [isMobileOpened])

  return (
    <MenuWrapper>
      <MenuLogo />
      {user && (
        <SubMenuContainer isMobileOpened={isMobileOpened} setIsMobileOpened={setIsMobileOpened}>
          {primaryMenu && <MenuPrimary primaryMenu={primaryMenu} />}
          {secondaryMenu && <MenuSecondary secondaryMenu={secondaryMenu} user={user} isMobileOpened={isMobileOpened} />}
        </SubMenuContainer>
      )}
    </MenuWrapper>
  )
}

export { Menu }
